/**
 * Meeting desk state shared by the Analysis → Package steps for one deal:
 * the bid, the ARO judgment, the desk's own forecast case, which case is
 * frozen for export, and the sent-export log. Per-browser convenience only
 * (localStorage, wrapped) — frozen cases also go to the scenario register.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const PREFIX = 'd2d.energy.meeting.';
const EVENT = 'd2d-meeting-desk';

export const DEFAULT_DESK = Object.freeze({
  // Null until the deal resolver fills it. $13MM is Eagle's ask, not a global bid.
  bidPriceMM: null,
  bidEdited: false,
  aroMM: null,
  custom: null,
  frozen: null,
  sent: [],
  forecastApproved: false,
});

function key(dealId) {
  return `${PREFIX}${dealId || 'default'}`;
}

export function readDesk(dealId) {
  try {
    const raw = window.localStorage.getItem(key(dealId));
    if (!raw) return { ...DEFAULT_DESK };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_DESK, ...(parsed && typeof parsed === 'object' ? parsed : {}) };
  } catch {
    return { ...DEFAULT_DESK };
  }
}

export function writeDesk(dealId, next) {
  try {
    window.localStorage.setItem(key(dealId), JSON.stringify(next));
  } catch {
    /* private mode / quota — state still lives for this page view */
  }
  try {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { dealId } }));
  } catch {
    /* ignore */
  }
}

/** React hook: [desk, patchDesk]. Stays in sync across steps and tabs. */
export function useMeetingDesk(dealId, seed = {}) {
  const init = () => {
    let hasStored = false;
    try {
      hasStored = Boolean(window.localStorage.getItem(key(dealId)));
    } catch {
      hasStored = false;
    }
    const stored = readDesk(dealId);
    return hasStored ? stored : { ...stored, ...seed };
  };
  const [desk, setDesk] = useState(init);
  const deskRef = useRef(desk);
  deskRef.current = desk;

  useEffect(() => {
    setDesk(init());
    const onChange = (e) => {
      if (!e?.detail || e.detail.dealId === dealId) setDesk(readDesk(dealId));
    };
    const onStorage = (e) => {
      if (e.key === key(dealId)) setDesk(readDesk(dealId));
    };
    window.addEventListener(EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
    // seed only matters when nothing is stored yet; re-run on deal switch only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  const patchDesk = useCallback(
    (patch) => {
      const prev = deskRef.current;
      const next = { ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) };
      deskRef.current = next;
      setDesk(next);
      writeDesk(dealId, next);
    },
    [dealId],
  );

  return [desk, patchDesk];
}
